import React, { useMemo, useState } from 'react';

import dayjs from '~/timeInit';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import FlightIcon from '@mui/icons-material/Flight';
import HotelIcon from '@mui/icons-material/Hotel';
import SailingIcon from '@mui/icons-material/Sailing';
import TourIcon from '@mui/icons-material/Tour';
import { Avatar, Box, Dialog, DialogContent, IconButton, Stack, Typography } from '@mui/material';

import type { PreviewPublicVoucher } from '~/services/PromoService';

type Props = {
  voucher: PreviewPublicVoucher;
};

function getVoucherProductIcon(productType?: string): typeof CardGiftcardIcon {
  switch (productType) {
    case 'flight':
      return FlightIcon;
    case 'hotel':
      return HotelIcon;
    case 'cruise':
      return SailingIcon;
    case 'tour':
      return TourIcon;
    case 'carhire':
      return DirectionsCarIcon;
    default:
      return CardGiftcardIcon;
  }
}

function getVoucherProductColor(productType?: string, theme?: string): string {
  if (theme === 'luxplus') {
    return 'warning.main';
  }
  switch (productType) {
    case 'flight':
      return 'primary.main';
    case 'hotel':
      return 'success.main';
    case 'cruise':
    case 'tour':
      return 'info.main';
    case 'carhire':
      return 'secondary.main';
    default:
      return 'success.main';
  }
}

function getVoucherValueLabel(voucher: PreviewPublicVoucher): React.ReactNode {
  if (voucher.voucherAmountDisplay !== undefined && voucher.voucherAmountDisplay !== null) {
    return voucher.voucherAmountDisplay.trim() ? voucher.voucherAmountDisplay : 'Voucher';
  }
  if (voucher.voucherDisplayAmount != null) {
    const amount = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: voucher.voucherCurrency,
      maximumFractionDigits: 0,
    }).format(voucher.voucherDisplayAmount);

    return voucher.voucherHasMultipleRewardAmounts ? `Value up to ${amount}` : amount;
  }

  const currencies = voucher.voucherCurrencies?.length
    ? voucher.voucherCurrencies
    : voucher.voucherCurrency
      ? [voucher.voucherCurrency]
      : [];

  return currencies.length > 0 ? `Issued in ${currencies.join(', ')}` : 'Voucher';
}

export default function VoucherPublicPreviewCard({ voucher }: Props) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const VoucherIcon = getVoucherProductIcon(voucher.productType);
  const iconColor = getVoucherProductColor(voucher.productType, voucher.theme);
  const title = voucher.voucherTitle?.trim() || voucher.voucherName;
  const valueLabel = useMemo(() => getVoucherValueLabel(voucher), [voucher]);
  const expiryLabel =
    voucher.expiresAt && dayjs(voucher.expiresAt).isValid() ? dayjs(voucher.expiresAt).format('D MMM YYYY') : '';
  const muted = voucher.voucherDisplayAmount == null && !voucher.voucherAmountDisplay;

  return (
    <>
      <Box
        sx={{
          border: 2,
          borderColor: muted ? 'divider' : 'text.primary',
          borderRadius: 7,
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 2.5 },
          opacity: muted ? 0.72 : 1,
          bgcolor: 'background.paper',
          maxWidth: 760,
        }}
      >
        <Stack direction="row" spacing={{ xs: 2, sm: 3 }} alignItems="center">
          <Stack direction="row" spacing={{ xs: 1.5, sm: 2.5 }} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
            <Avatar
              sx={{
                width: { xs: 44, sm: 52 },
                height: { xs: 44, sm: 52 },
                bgcolor: iconColor,
                color: 'common.white',
                flexShrink: 0,
              }}
            >
              <VoucherIcon fontSize="medium" />
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="h6"
                color={voucher.productType === 'flight' ? 'primary.main' : 'text.secondary'}
                sx={{
                  lineHeight: 1.15,
                  overflowWrap: 'anywhere',
                  textTransform: voucher.productType === 'flight' ? 'uppercase' : undefined,
                }}
              >
                {title}
              </Typography>
              <Typography
                variant="h5"
                fontWeight={700}
                color="text.secondary"
                sx={{ lineHeight: 1.25, overflowWrap: 'anywhere' }}
              >
                {valueLabel}
              </Typography>
              {expiryLabel ? (
                <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
                  Expires: {expiryLabel} ·{' '}
                  <Box
                    component="button"
                    type="button"
                    onClick={() => setDetailsOpen(true)}
                    sx={{
                      p: 0,
                      border: 0,
                      bgcolor: 'transparent',
                      color: 'text.primary',
                      cursor: 'pointer',
                      font: 'inherit',
                      textDecoration: 'underline',
                    }}
                  >
                    Details
                  </Box>
                </Typography>
              ) : null}
            </Box>
          </Stack>
          <CheckCircleIcon sx={{ color: 'text.primary', flexShrink: 0 }} />
        </Stack>
      </Box>
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogContent sx={{ px: { xs: 3, sm: 6 }, py: { xs: 3, sm: 5 } }}>
          <Stack spacing={3}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
              <Stack direction="row" alignItems="center" spacing={{ xs: 2, sm: 4 }} sx={{ minWidth: 0 }}>
                <IconButton aria-label="Back" onClick={() => setDetailsOpen(false)} edge="start">
                  <ArrowBackIcon />
                </IconButton>
                <Typography variant="h3" fontWeight={700} sx={{ overflowWrap: 'anywhere', textTransform: 'uppercase' }}>
                  {title}
                </Typography>
              </Stack>
              <IconButton aria-label="Close" onClick={() => setDetailsOpen(false)} edge="end">
                <CloseIcon />
              </IconButton>
            </Stack>
            {expiryLabel ? (
              <Typography variant="h6" color="text.secondary">
                Expires: {expiryLabel}
              </Typography>
            ) : null}
            {voucher.voucherDescription?.trim() ? (
              <Typography variant="h6" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                {voucher.voucherDescription}
              </Typography>
            ) : null}
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}
